import type { Theme } from 'foundations-theming-base/types';
import type { ThemeSettings, ThemeVersion } from './getLocalThemeSettings';
import { getHostname } from './utils';

type ChosenTheme = 'canvas-light' | 'trellis-light' | 'trellis-dark';
type MultiTheme = {
  ['canvas-light']: Theme;
  ['trellis-light']: Theme;
  ['trellis-dark']: Theme;
};
type CanvasTheme = {
  themeName: 'canvas';
  themeMode: 'light';
};

type TrellisTheme = {
  themeName: 'trellis';
  themeMode: 'light' | 'dark';
};

type ThemeParams = (CanvasTheme | TrellisTheme) & {
  themeVersion: ThemeVersion;
};

function validateThemeParams(themeParams: {
  themeName: string;
  themeMode: string;
  themeVersion: ThemeVersion;
}): asserts themeParams is ThemeParams {
  const { themeName, themeMode } = themeParams;
  if (!['canvas', 'trellis'].includes(themeName)) {
    throw new Error(`Invalid theme name: ${themeName}`);
  }
  if (!['light', 'dark'].includes(themeMode)) {
    throw new Error(`Invalid theme mode: ${themeMode}`);
  }
  if (themeName === 'canvas' && themeMode === 'dark') {
    throw new Error(`Canvas theme does not support dark mode`);
  }
}

// id rather not add an enviro dep, so get the hublet manually
export function getUrlDetails(themeVersion: ThemeVersion) {
  const regexp = /^(?:app|local)(?:-([a-z]+[0-9]))?.hubspot(qa)?\.com/;
  const baseUrl = getHostname();
  const match = baseUrl.match(regexp);
  if (!match) {
    return { hublet: '', suffix: '' };
  }
  const hublet = match[1] || '';

  let suffix = match[2] || '';
  if (themeVersion.type === 'deployed') {
    suffix = themeVersion?.env === 'qa' ? 'qa' : '';
  }

  return { hublet, suffix };
}

const getDeployedThemeUrl = (themeVersion: ThemeVersion) => {
  const { hublet, suffix } = getUrlDetails(themeVersion);

  if (themeVersion.type === 'default') {
    throw new Error(`${themeVersion.type} themes do not have deployed URLs.`);
  }

  if (themeVersion.type === 'version') {
    return `https://static.hsappstatic.net/foundations-theming-base/static-${themeVersion.version}/bundles/multi-theme-${themeVersion.channel}.js`;
  }

  const subdomain =
    themeVersion.type === 'local' ? `local${hublet}` : `app${hublet}`;

  return `https://${subdomain}.hubspot${suffix}.com/foundations-theming-base/theme-${themeVersion.channel}.js`;
};

export const getDeployedTheme = async (themeSettings: ThemeSettings) => {
  validateThemeParams(themeSettings);
  const { themeName, themeMode, themeVersion } = themeSettings;
  const chosenTheme = `${themeName}-${themeMode}` as ChosenTheme;
  const themeUrl = getDeployedThemeUrl(themeVersion);

  const deployedTheme: MultiTheme = await import(
    /* webpackIgnore: true */ themeUrl
  )
    .then((m) => m.default)
    .catch((err) => {
      throw new Error(`Failed to load theme from ${themeUrl}: ${err.message}`);
    });

  const theme = deployedTheme[chosenTheme];
  if (!theme) {
    throw new Error('Unsupported theme name and mode combination.');
  }

  return theme;
};
